import { useLazyQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import getUserStatus from "../../../../GraphQL/Query/getUserStatus";
import { noop } from "../../../../utils/noop";
import { Button, ButtonLoading } from "../../../SharedComponents/Button/Button";
import { FormInput } from "../../../SharedComponents/FormInput/FormInput";
import { InlineInfoBox } from "../../../SharedComponents/InlineInfoBox/InlineInfoBox";
import { LoginOnboardingInterstitial } from "../LoginOnboardingInterstitial/LoginOnboardingInterstitial";

/**
 * @typedef {'IDLE' | 'ONBOARDING' | 'USER_NOT_FOUND' | 'LOADING'} ComponentState
 */

export const LoginOnboardingEmailInput = ({
  defaultEmail,
  onUserShouldBeOnboarded = noop,
  onUserShouldBeLoggedIn = noop,
}) => {
  /** @type {[ComponentState, (state: ComponentState) => void]} */
  const [componentState, setComponentState] = useState("IDLE");
  const [email, setEmail] = useState(defaultEmail);

  const { t } = useTranslation();

  // Set up fetching of user onboarded status
  const [getUserOnboardedStatus] = useLazyQuery(getUserStatus, {
    fetchPolicy: "network-only",
    onCompleted: onUserFound,
    onError: onUserError,
  });

  // Event handlers
  function onEmailSubmit(email) {
    setEmail(email);
    getUserOnboardedStatus({ variables: { email } });
  }

  function onUserFound(resultData) {
    // Switched around - If the status is active, they can log in, otherwise, it's a good ol' onboarding flow.
    if (resultData.userStatus.status === "ACTIVE") {
      // The user is already onboarded

      onUserShouldBeLoggedIn(email);
    } else {
      // The user should be onboarded
      setComponentState("ONBOARDING");
    }
  }

  function onUserError() {
    setComponentState("USER_NOT_FOUND");
  }

  function onRenewButtonClick() {
    onUserShouldBeOnboarded(email);
  }

  // Formik form configuration
  const formConfig = {
    initialValues: {
      email: defaultEmail,
    },
    onSubmit: (values) => onEmailSubmit(values.email),
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("validation.emailInvalid")
        .required("validation.emailRequired"),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  };

  // View state booleans
  const isEmailEnabled = ["IDLE", "USER_NOT_FOUND"].includes(componentState);
  const isLoading = ["LOADING"].includes(componentState);
  const showUserNotFound = ["USER_NOT_FOUND"].includes(componentState);
  const showOnboardingInterstitial = ["ONBOARDING"].includes(componentState);
  const showSubmitButton = ["IDLE", "USER_NOT_FOUND", "LOADING"].includes(
    componentState
  );

  return (
    <Formik {...formConfig}>
      {() => (
        <Form>
          <FormInput
            name="email"
            type="text"
            label="generic.email"
            disabled={!isEmailEnabled}
          />
          {showUserNotFound && (
            <InlineInfoBox type="info">
              {t("onboardingForm.userNotFound")}
            </InlineInfoBox>
          )}
          {showOnboardingInterstitial && (
            <LoginOnboardingInterstitial onButtonClick={onRenewButtonClick} />
          )}
          {showSubmitButton && (
            <div className="LoginOnboardingForm__submit">
              <div className="LoginOnboardingForm__submit-button">
                <Button
                  buttonStyle="Button__primary"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading && <ButtonLoading />}
                  {t("generic.next")}
                </Button>
              </div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
